import useRequest from "@/lib/request/useRequest";
import { FieldConnection } from "@/types/connection";
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    DateRangePicker,
    Card,
    CardBody,
    Checkbox,
    Divider,
    RangeValue,
    DateValue,
    ButtonGroup,
} from "@heroui/react";
import { parseDate } from "@internationalized/date";
import { useState, useMemo } from "react";
import dayjs from "dayjs";
import johnDeereLogo from "@/assets/images/JDOC-tm-default.svg";
import { Platform } from "@/types/enums";

type JohnDeereOperation = {
    id: string;
    startDate: string;
    type: string;
    cropName: string;
    products: {
        name: string;
    }[];
};

function ImportOperation({
    connection,
    onClose,
}: {
    connection: FieldConnection;
    onClose: () => void;
}) {
    const { useGetRequest, useMutateRequest } = useRequest();
    const importOperationMutation = useMutateRequest({
        method: "post",
        url: `field_connections/${connection?.id}/operations/`,
        successMessage: "Operations imported successfully",
        errorMessage: "Failed to import operations",
        onSuccess: () => {
            onClose();

        },
    });
    const operationsQuery = useGetRequest<JohnDeereOperation[]>({
        url: `field_connections/${connection?.id}/operations/`,
        enabled: !!connection?.id,
    });
    const [selectedOperations, setSelectedOperations] = useState<string[]>([]);
    const [dateRange, setDateRange] = useState<RangeValue<DateValue> | null>({
        start: parseDate(dayjs().subtract(8, "months").format("YYYY-MM-DD")),
        end: parseDate(dayjs().format("YYYY-MM-DD")),
    });
    const [selectedType, setSelectedType] = useState<string>("all");

    const filteredOperations = useMemo(() => {
        return operationsQuery.data?.filter((operation) => {
            return selectedType !== "all" ? (
                operation.type.toLowerCase() === selectedType
                && dayjs(operation.startDate).isAfter(
                    dayjs(dateRange?.start?.toString() ?? dayjs().subtract(8, "months").format("YYYY-MM-DD")),
                ) &&
                dayjs(operation.startDate).isBefore(
                    dayjs(dateRange?.end?.toString() ?? dayjs().format("YYYY-MM-DD")),
                )
            ) : dayjs(operation.startDate).isAfter(
                dayjs(dateRange?.start?.toString() ?? ""),
            ) &&
            dayjs(operation.startDate).isBefore(
                dayjs(dateRange?.end?.toString() ?? ""),
            )
        });
    }, [operationsQuery.data, selectedType, dateRange]);

    return (
        <Modal isOpen onClose={onClose} size="3xl" scrollBehavior="inside" className="h-[60vh]">
            <ModalContent>
                <ModalHeader className="flex flex-row gap-2 items-center">
                    <p className="text-lg">Import Operation</p>{" "}
                    {connection.user_connection.platform ===
                        Platform.JohnDeere && (
                            <img
                                src={johnDeereLogo}
                                alt="John Deere"
                                className="h-12 w-auto object-contain"
                            />
                        )}
                </ModalHeader>
                <ModalBody className="flex flex-col gap-2 mx-1">
                    <div className="flex flex-row gap-4">
                        <Checkbox
                            isSelected={
                                selectedOperations.length ===
                                filteredOperations?.length
                            }
                            onValueChange={() => {
                                setSelectedOperations(
                                    selectedOperations.length ===
                                        filteredOperations?.length
                                        ? []
                                        : (filteredOperations?.map(
                                            (operation) => operation.id,
                                        ) ?? []),
                                );
                            }}
                        />
                        <DateRangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            showMonthAndYearPickers
                        />
                        <ButtonGroup>
                            <Button
                                color={
                                    selectedType === "all"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setSelectedType("all")}
                            >
                                All
                            </Button>
                            <Button
                                color={
                                    selectedType === "planting"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setSelectedType("planting")}
                            >
                                Planting
                            </Button>
                            <Button
                                color={
                                    selectedType === "application"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setSelectedType("application")}
                            >
                                Application
                            </Button>
                            <Button
                                color={
                                    selectedType === "harvest"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setSelectedType("harvest")}
                            >
                                Harvest
                            </Button>
                        </ButtonGroup>
                    </div>
                    <Divider />
                    {operationsQuery.isLoading && <Spinner />}
                    {!operationsQuery.isLoading &&
                        filteredOperations?.length === 0 && (
                            <div className="flex flex-col gap-2">
                                <p className="text-center text-gray-500">
                                    No operations found
                                </p>
                            </div>
                        )}
                    <div className="flex flex-col gap-2">
                        {filteredOperations?.map((operation) => (
                            <Card key={operation.id}>
                                <CardBody className="flex flex-row gap-2">
                                    <Checkbox
                                        isSelected={selectedOperations.includes(
                                            operation.id,
                                        )}
                                        onValueChange={() => {
                                            setSelectedOperations(
                                                selectedOperations.includes(
                                                    operation.id,
                                                )
                                                    ? selectedOperations.filter(
                                                        (id) =>
                                                            id !==
                                                            operation.id,
                                                    )
                                                    : [
                                                        ...selectedOperations,
                                                        operation.id,
                                                    ],
                                            );
                                        }}
                                    />
                                    {dayjs(operation.startDate).format(
                                        "YYYY-MM-DD",
                                    )}{" "}
                                    - {operation.type} (
                                    {operation.type === "Application"
                                        ? operation.products
                                            ?.map((product) => product.name)
                                            .join(", ")
                                        : operation.cropName}
                                    )
                                </CardBody>
                            </Card>
                        ))}
                    </div>
                </ModalBody>
                <ModalFooter className="flex flex-row items-center">
                    <div className="text-sm text-warning-500">
                        File processing may take a few minutes and will be completed in the background.
                    </div>
                    <Button
                        color="primary"
                        isDisabled={selectedOperations.length === 0}
                        onPress={() => importOperationMutation.mutate({
                            url: `field_connections/${connection?.id}/operations/`,
                            method: "post",
                            data: selectedOperations,
                        })}
                        isLoading={importOperationMutation.isPending}
                        className="flex-1"
                    >
                        Import
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default ImportOperation;
